import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, Check, Undo2, FileText, CircleDollarSign, AlertCircle, Receipt } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { api, API_BASE } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { getToken } from '@/lib/auth';
import { logger } from '@/lib/logger';
import AdminTabs from '@/components/admin/AdminTabs';
import AdminSearch from '@/components/admin/AdminSearch';
import EmptyState from '@/components/admin/EmptyState';

const STATUSES = ['All', 'Unpaid', 'Paid', 'Void'];

const money = (v) => `SAR ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusStyle = (s) => ({
  Unpaid: 'bg-red-500/15 text-red-300 border-red-500/30',
  Paid: 'bg-green-500/15 text-green-300 border-green-500/30',
  Void: 'bg-white/5 text-white/50 border-white/10',
}[s] || 'bg-white/5 text-white/60 border-white/10');

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/invoices');
      setItems(data || []);
    } catch (err) { logger.warn('Failed to load invoices', err); toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((i) => {
      if (statusFilter !== 'All' && i.status !== statusFilter) return false;
      if (!term) return true;
      return (
        i.invoice_number?.toLowerCase().includes(term) ||
        i.client_name?.toLowerCase().includes(term) ||
        i.session_name?.toLowerCase().includes(term)
      );
    });
  }, [items, statusFilter, q]);

  const kpis = useMemo(() => {
    const nonVoid = items.filter((i) => i.status !== 'Void');
    const total = nonVoid.reduce((sum, i) => sum + Number(i.amount || 0), 0);
    const paid = nonVoid.filter((i) => i.status === 'Paid').reduce((sum, i) => sum + Number(i.amount || 0), 0);
    return {
      total,
      paid,
      outstanding: Math.max(0, total - paid),
      unpaid_count: nonVoid.filter((i) => i.status === 'Unpaid').length,
      paid_count: nonVoid.filter((i) => i.status === 'Paid').length,
    };
  }, [items]);

  const setStatus = async (id, next) => {
    try {
      await api.patch(`/admin/invoices/${id}`, { status: next });
      toast.success(next === 'Paid' ? 'Marked as paid.' : 'Marked as unpaid.');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to update'); }
  };

  const downloadPdf = async (inv) => {
    try {
      const url = `${API_BASE}/admin/invoices/${inv.id}/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error('Failed to download');
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${inv.invoice_number || 'invoice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast.error(err.message || 'Could not download PDF');
    }
  };

  return (
    <div data-testid={ADMIN.invoicesPage}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight inline-flex items-center gap-2"><FileText className="w-7 h-7 text-[var(--pg-admin-accent)]" /> Invoices</h1>
          <p className="mt-1 text-sm text-white/60">Track payments per client. Auto-created when sessions are confirmed.</p>
        </div>
      </div>

      {/* KPIs — 2-col on mobile, 4-col on desktop */}
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
        <Kpi label="Outstanding" value={money(kpis.outstanding)} icon={AlertCircle} tone="danger" />
        <Kpi label="Collected" value={money(kpis.paid)} icon={CircleDollarSign} tone="ok" />
        <Kpi label="Unpaid" value={kpis.unpaid_count} icon={Receipt} tone="warn" sub="invoices" />
        <Kpi label="Paid" value={kpis.paid_count} icon={Check} tone="ok" sub="invoices" />
      </div>

      {/* Filters — stack vertically on mobile so the layout never shifts */}
      <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
        <div className="order-2 sm:order-1 sm:flex-1 sm:min-w-[200px] w-full">
          <AdminSearch
            testid={ADMIN.invoiceSearch}
            value={q}
            onChange={setQ}
            placeholder="Search by invoice #, client, or session..."
          />
        </div>
        <div className="order-1 sm:order-2 self-start">
          <AdminTabs
            testid={ADMIN.invoiceStatusFilter}
            options={STATUSES.map((s) => ({
              id: s,
              label: s,
              count: s === 'All' ? undefined : items.filter((i) => i.status === s).length,
            }))}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>
      </div>

      {/* Table / list */}
      <div data-testid={ADMIN.invoicesTable} className="mt-5 rounded-2xl border border-white/10 bg-[#141414] overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-white/60">Loading...</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No invoices match this filter"
            description="Invoices are auto-generated the moment you confirm a session. Try a different filter or confirm a new session to see them appear here."
            icon={FileText}
          />
        ) : (
          <>
            {/* Desktop table */}
            <table className="hidden md:table admin-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Session</th>
                  <th>Issued</th>
                  <th className="text-right">Amount</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.id} data-testid={ADMIN.invoiceRow(i.id)}>
                    <td className="font-mono text-xs text-white/85">{i.invoice_number}</td>
                    <td>{i.client_name || '\u2014'}</td>
                    <td className="text-white/75">{i.session_name || '\u2014'}</td>
                    <td className="text-white/60 text-xs">{i.issued_at ? format(parseISO(i.issued_at), 'MMM d, yyyy') : '\u2014'}</td>
                    <td className="text-right font-semibold">{money(i.amount)}</td>
                    <td className="text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${statusStyle(i.status)}`}>{i.status}</span>
                    </td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {i.status !== 'Paid' && i.status !== 'Void' && (
                          <button
                            data-testid={ADMIN.invoiceMarkPaid(i.id)}
                            onClick={() => setStatus(i.id, 'Paid')}
                            className="p-1.5 rounded-lg hover:bg-green-500/10 text-green-300"
                            title="Mark as paid"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        {i.status === 'Paid' && (
                          <button
                            data-testid={ADMIN.invoiceMarkUnpaid(i.id)}
                            onClick={() => setStatus(i.id, 'Unpaid')}
                            className="p-1.5 rounded-lg hover:bg-white/5 text-white/70"
                            title="Mark as unpaid"
                          >
                            <Undo2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          data-testid={ADMIN.invoiceDownloadPdf(i.id)}
                          onClick={() => downloadPdf(i)}
                          className="p-1.5 rounded-lg hover:bg-white/5 text-white/80"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile list */}
            <ul className="md:hidden divide-y divide-white/5">
              {filtered.map((i) => (
                <li key={i.id} data-testid={ADMIN.invoiceRow(i.id)} className="p-4 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[11px] font-mono text-white/60 truncate">{i.invoice_number}</div>
                      <div className="font-semibold truncate">{i.client_name || '\u2014'}</div>
                      <div className="text-xs text-white/60 truncate">{i.session_name || '\u2014'}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold">{money(i.amount)}</div>
                      <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${statusStyle(i.status)}`}>{i.status}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-white/50">
                    <span>{i.issued_at ? format(parseISO(i.issued_at), 'MMM d, yyyy') : ''}</span>
                    <div className="inline-flex gap-1">
                      {i.status !== 'Paid' && i.status !== 'Void' && (
                        <button data-testid={ADMIN.invoiceMarkPaid(i.id)} onClick={() => setStatus(i.id, 'Paid')} className="p-1.5 rounded-lg bg-green-500/10 text-green-300"><Check className="w-4 h-4" /></button>
                      )}
                      {i.status === 'Paid' && (
                        <button data-testid={ADMIN.invoiceMarkUnpaid(i.id)} onClick={() => setStatus(i.id, 'Unpaid')} className="p-1.5 rounded-lg bg-white/5 text-white/70"><Undo2 className="w-4 h-4" /></button>
                      )}
                      <button data-testid={ADMIN.invoiceDownloadPdf(i.id)} onClick={() => downloadPdf(i)} className="p-1.5 rounded-lg bg-white/5 text-white/80"><Download className="w-4 h-4" /></button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

const Kpi = ({ label, value, icon: Icon, tone = 'default', sub }) => {
  const toneClass = {
    default: 'bg-white/5 text-white',
    ok: 'bg-green-500/15 text-green-300',
    warn: 'bg-[var(--pg-admin-accent-soft)] text-[var(--pg-admin-accent)]',
    danger: 'bg-red-500/15 text-red-300',
  }[tone];
  return (
    <div className="rounded-2xl border border-white/10 bg-[#141414] p-3 sm:p-4 flex items-center gap-2.5 sm:gap-3">
      <div className={`inline-flex w-9 h-9 sm:w-10 sm:h-10 items-center justify-center rounded-xl shrink-0 ${toneClass}`}><Icon className="w-4 h-4 sm:w-5 sm:h-5" /></div>
      <div className="min-w-0">
        <div className="font-display text-base sm:text-xl leading-none truncate">{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{label}</div>
        {sub && <div className="text-[10px] text-white/40 mt-0.5 hidden sm:block">{sub}</div>}
      </div>
    </div>
  );
};
